import { clientLogger } from '@/lib/clientLogger';
import { App } from '@capacitor/app';
import { CapacitorHttp } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { ANDROID_VERSION_CODE, BUILTIN_APP_VERSION, compareSemver } from './appVersion';
import { getAppOrigin, isAndroid, isNative } from './capacitor';

type Release = {
  version: string;
  url: string;
  checksum: string;
  size?: number;
  // The exact Android APK versionCode this bundle targets. A device only takes a release
  // whose androidVersionCode equals its own installed versionCode.
  androidVersionCode: number;
  releasedAt?: string;
};

type Manifest = {
  releases: Release[];
};

export type OtaNotifier = (notification: {
  type: 'info' | 'success' | 'error' | 'warning';
  title: string;
  message?: string;
}) => void;

const FALLBACK_PUBLIC_URL = 'https://app.servicestrike.com';
const OTA_MANIFEST_PATH = '/ota/android/latest.json';

let inflightCheck: Promise<void> | null = null;
let appReadyPromise: Promise<void> | null = null;
let pendingBundleVersion: string | null = null;

function getManifestUrl(): string {
  const origin = (getAppOrigin() || FALLBACK_PUBLIC_URL).replace(/\/+$/, '');
  return `${origin}${OTA_MANIFEST_PATH}`;
}

// The device's actual installed APK versionCode (App.getInfo().build), falling back to
// the build-time constant if it can't be read. This is what floor checks must compare against.
async function getDeviceVersionCode(): Promise<number> {
  try {
    const info = await App.getInfo();
    const code = Number.parseInt(info.build, 10);
    if (!Number.isNaN(code)) {
      return code;
    }
  } catch (error) {
    clientLogger.debug('[OTA] Failed to read native versionCode; using build-time constant:', error);
  }

  return ANDROID_VERSION_CODE;
}

function resolveRunningVersion(version?: string | null): string {
  if (!version || version === 'builtin') {
    return BUILTIN_APP_VERSION;
  }

  return version;
}

function isRelease(value: unknown): value is Release {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const release = value as Record<string, unknown>;

  return (
    typeof release.version === 'string' &&
    typeof release.url === 'string' &&
    typeof release.checksum === 'string' &&
    typeof release.androidVersionCode === 'number'
  );
}

function isManifest(value: unknown): value is Manifest {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const releases = (value as Record<string, unknown>).releases;

  return Array.isArray(releases) && releases.every(isRelease);
}

// Picks the release whose androidVersionCode EXACTLY matches the device's installed APK
// versionCode. If several share that code (shouldn't happen — deploys upsert by code),
// the newest version wins. Returns null when no track targets this exact code.
//
// `deviceVersionCode` MUST be the device's actual installed APK versionCode (from
// App.getInfo().build), not the build-time __ANDROID_VERSION_CODE__ constant: that
// constant reflects the build.gradle value when *this* JS bundle was built, which for
// an OTA bundle can differ from the device's real native code and would mis-route tracks.
function selectRelease(manifest: Manifest, deviceVersionCode: number): Release | null {
  const matches = manifest.releases.filter(
    (release) => release.androidVersionCode === deviceVersionCode,
  );

  if (matches.length === 0) {
    return null;
  }

  return matches.reduce((best, release) =>
    compareSemver(release.version, best.version) > 0 ? release : best,
  );
}

async function fetchManifest(): Promise<Manifest | null> {
  const manifestUrl = `${getManifestUrl()}?t=${Date.now()}`;
  let data: unknown;

  try {
    if (isNative() && isAndroid()) {
      clientLogger.info('[OTA] Fetching manifest via CapacitorHttp from', manifestUrl);
      const response = await CapacitorHttp.get({
        url: manifestUrl,
        headers: {
          Accept: 'application/json',
        },
        readTimeout: 10000,
        connectTimeout: 10000,
        disableRedirects: false,
      });

      clientLogger.info('[OTA] CapacitorHttp response status:', response.status);

      if (response.status < 200 || response.status >= 300) {
        clientLogger.debug('[OTA] Native manifest request failed with status', response.status);
        return null;
      }

      data = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    } else {
      clientLogger.info('[OTA] Fetching manifest via fetch from', manifestUrl);
      const response = await fetch(manifestUrl, {
        cache: 'no-store',
      });

      clientLogger.info('[OTA] Fetch response status:', response.status);

      if (!response.ok) {
        clientLogger.debug('[OTA] Manifest request failed with status', response.status);
        return null;
      }

      data = await response.json();
    }

    clientLogger.info('[OTA] Manifest data received:', data);

    if (!isManifest(data)) {
      clientLogger.debug('[OTA] Manifest payload is invalid', data);
      return null;
    }

    return data;
  } catch (error) {
    clientLogger.debug('[OTA] Manifest fetch failed:', error);
    return null;
  }
}

export async function checkAndInstall(notify: OtaNotifier): Promise<void> {
  if (!isNative() || !isAndroid()) {
    return;
  }

  if (inflightCheck) {
    return inflightCheck;
  }

  inflightCheck = (async () => {
    try {
      clientLogger.info('[OTA] Starting update check and install');
      const manifest = await fetchManifest();

      if (!manifest) {
        clientLogger.info('[OTA] No manifest available, skipping update check');
        return;
      }

      const deviceVersionCode = await getDeviceVersionCode();
      const release = selectRelease(manifest, deviceVersionCode);

      if (!release) {
        clientLogger.info(
          `[OTA] No release targets Android versionCode ${deviceVersionCode}`,
        );
        return;
      }

      clientLogger.info('[OTA] Selected release version:', release.version);

      if (pendingBundleVersion === release.version) {
        clientLogger.info('[OTA] Skipping, already have pending bundle', release.version);
        return;
      }

      clientLogger.info('[OTA] Getting current bundle version');
      const current = await CapacitorUpdater.current();
      // Builtin reports its native versionName ("1.0") as `version`; identify it by id.
      const runningVersion =
        current.bundle.id === 'builtin'
          ? BUILTIN_APP_VERSION
          : resolveRunningVersion(current.bundle.version);
      clientLogger.info('[OTA] Current running version:', runningVersion);

      if (compareSemver(runningVersion, release.version) >= 0) {
        clientLogger.info('[OTA] Already running latest version', runningVersion);
        return;
      }

      notify({
        type: 'info',
        title: 'Update available',
        message: `Downloading v${release.version}...`,
      });

      clientLogger.info('[OTA] Starting download of v' + release.version);
      const bundle = await CapacitorUpdater.download({
        url: release.url,
        version: release.version,
        checksum: release.checksum,
      });

      clientLogger.info('[OTA] Download complete, setting next bundle', bundle.id);
      await CapacitorUpdater.next({ id: bundle.id });
      pendingBundleVersion = release.version;

      notify({
        type: 'success',
        title: 'Update ready',
        message: `v${release.version} will apply on next launch.`,
      });
    } catch (error) {
      clientLogger.debug('[OTA] Failed to check or install update:', error);
    }
  })()
    .finally(() => {
      inflightCheck = null;
    });

  return inflightCheck;
}

export function attachOtaLifecycleListeners(notify: OtaNotifier): () => void {
  if (!isNative() || !isAndroid()) {
    return () => {};
  }

  const listeners = [
    CapacitorUpdater.addListener('downloadFailed', (event) => {
      pendingBundleVersion = null;
      clientLogger.debug('[OTA] downloadFailed', event);
      notify({
        type: 'error',
        title: 'Update failed',
        message: 'Could not download the latest update.',
      });
    }),
    CapacitorUpdater.addListener('updateFailed', (event) => {
      pendingBundleVersion = null;
      clientLogger.debug('[OTA] updateFailed', event);
      notify({
        type: 'error',
        title: 'Update failed',
        message: event.bundle?.version
          ? `v${event.bundle.version} could not be installed.`
          : 'The downloaded update could not be installed.',
      });
    }),
    CapacitorUpdater.addListener('appReloaded', () => {
      pendingBundleVersion = null;
      clientLogger.info('[OTA] WebView reloaded into a new bundle');
    }),
  ];

  return () => {
    void Promise.allSettled(listeners).then((results) => {
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          void result.value.remove();
        }
      });
    });
  };
}

export async function signalAppReady(): Promise<void> {
  if (!isNative() || !isAndroid()) {
    return;
  }

  if (!appReadyPromise) {
    appReadyPromise = CapacitorUpdater.notifyAppReady()
      .then(() => undefined)
      .catch((error) => {
        clientLogger.debug('[OTA] notifyAppReady failed:', error);
        appReadyPromise = null;
      });
  }

  const readyPromise = appReadyPromise;
  return readyPromise ?? Promise.resolve();
}